/**
 * The small vocabulary every screen shares: lamps, load phases, empty states.
 *
 * §16.5 forbids colour as the sole indicator, so a `Chip` always renders a word
 * beside its lamp — the type makes the word non-optional rather than trusting
 * each call site to remember.
 */

import type { ReactNode } from 'react';
import { Button, Flash, Label } from '@primer/react';

import type { Tone } from '../../logic/display-matrix.js';
import { Orb } from './orb.js';

/* Our seven §16.3 tones, onto Primer Label variants — one mapping, every
   chip on every screen rides it. The word stays mandatory (§16.5). */
const TONE_VARIANTS: Readonly<Record<Tone, 'secondary' | 'accent' | 'success' | 'attention' | 'severe' | 'danger'>> = {
  neutral: 'secondary',
  info: 'accent',
  active: 'success',
  wait: 'attention',
  attention: 'severe',
  unknown: 'secondary',
  danger: 'danger',
};

export function Chip({ tone, text, label }: { tone: Tone; text: string; label?: string }): ReactNode {
  return (
    <Label variant={TONE_VARIANTS[tone]} aria-label={label ?? text}>
      {text}
    </Label>
  );
}

/**
 * What a query is doing, as something to render.
 *
 * Returns `null` when there is data to show, so a caller reads:
 * `const blocking = <QueryPhase …/>; return blocking ?? <TheRealThing/>`.
 */
export function QueryPhase({
  isPending,
  error,
  isEmpty,
  source,
  verb = 'Reading',
  emptyTitle,
  emptyDetail,
  onRetry,
  rows = 3,
}: {
  isPending: boolean;
  error: unknown;
  isEmpty?: boolean;
  source: string;
  verb?: string;
  emptyTitle?: string;
  emptyDetail?: string;
  onRetry?: () => void;
  rows?: number;
}): ReactNode {
  if (isPending) {
    return (
      <div className="skeleton" aria-busy="true">
        <p className="skeleton-source">
          <Orb size={20} state="searching" />
          {verb} {source}…
        </p>
        {Array.from({ length: rows }, (_, index) => (
          <div className="skeleton-row" key={index} />
        ))}
      </div>
    );
  }

  if (error !== null && error !== undefined) {
    return (
      <Flash variant="danger" role="alert">
        {source}: {error instanceof Error ? error.message : String(error)}
        {onRetry === undefined ? null : (
          <Button onClick={onRetry} size="small" style={{ marginLeft: 'var(--gap-3)' }}>
            Retry
          </Button>
        )}
      </Flash>
    );
  }

  if (isEmpty === true) {
    return (
      <div className="placeholder placeholder-empty">
        <p>
          <strong>{emptyTitle ?? 'Nothing here'}</strong>
        </p>
        {emptyDetail === undefined ? null : <p>{emptyDetail}</p>}
      </div>
    );
  }

  return null;
}

export function Section({
  label,
  children,
  lede,
}: {
  label: string;
  lede?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="panel" aria-label={label}>
      <h2>{label}</h2>
      {lede === undefined ? null : <p className="panel-lede">{lede}</p>}
      {children}
    </section>
  );
}
